"use client";

import { ScanDownloadPageContent } from "@/components/ScanDownloadPageContent";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { usePostHogPageView } from "@/lib/usePostHogPageView";
import { capturePostHogEvent } from "@/lib/posthog-client";
import { POSTHOG_EVENTS, POSTHOG_SOURCE } from "@/lib/posthog-events";

function sanitizeTargetPath(rawTarget: string | null): string | null {
  if (!rawTarget) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(rawTarget).trim();
  } catch {
    return null;
  }
  if (!decoded.startsWith("/")) return null;
  if (decoded.startsWith("//")) return null;
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(decoded)) return null;
  if (/^\/m\/[a-zA-Z0-9_-]+$/.test(decoded)) return decoded;
  if (/^\/l\/[a-zA-Z0-9_-]+$/.test(decoded)) return decoded;
  if (/^\/lists\/join\/[a-zA-Z0-9_-]+$/.test(decoded)) return decoded;
  return null;
}

export default function DownloadClient() {
  const searchParams = useSearchParams();
  const target = searchParams?.get("target") ?? null;
  const restaurantId = searchParams?.get("restaurant_id") ?? null;
  const status = searchParams?.get("status") ?? null;
  const sessionId = searchParams?.get("session_id") ?? null;
  const [targetPath, setTargetPath] = useState<string | null>(null);
  const checkoutCompletedFired = useRef(false);

  usePostHogPageView(POSTHOG_EVENTS.PRICING_VIEWED, {
    source: POSTHOG_SOURCE,
    page: "download",
    ...(restaurantId ? { restaurant_id: restaurantId } : {}),
  });

  useEffect(() => {
    if (checkoutCompletedFired.current || status !== "success" || !sessionId) return;
    checkoutCompletedFired.current = true;
    capturePostHogEvent(POSTHOG_EVENTS.SUBSCRIPTION_CHECKOUT_COMPLETED, {
      source: POSTHOG_SOURCE,
      ...(restaurantId ? { restaurant_id: restaurantId } : {}),
    });
  }, [status, sessionId, restaurantId]);

  useEffect(() => {
    setTargetPath(sanitizeTargetPath(target));
  }, [target]);

  return (
    <ScanDownloadPageContent
      source="download"
      preserveParams={searchParams ?? null}
      targetPath={targetPath}
    />
  );
}
